import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { useRouter } from '@wbk/config';
import { SearchIcon } from '@wbk/svg';
import { Button, ImageWithPlaceholder, Input, Select, SelectOption, Typography } from '@wbk/ui';
import { type WebSection } from '@wbk/contentful/api';
import { sanitizeInput } from '@/utils';
import useExploreContext from '@/context/explore/useExplore';
import HeroTitle from '../ui/HeroTitle';

type Props = {
  section?: WebSection;
};
const ExploreHeroSection = ({ section }: Props) => {
  const { t } = useTranslation('common');
  const { locale } = useRouter();
  const [searchParams, setSearch] = useSearchParams();
  const search = searchParams.get('q') || '';

  const {
    targetRef,
    selectedCategory,
    sortBy,
    sortOptions,
    selectedZone,
    categoryOptions,
    zoneOptions,
  } = useExploreContext();

  const { push, pathname } = useRouter();

  const [searchTerm, setSearchTerm] = useState(search);

  useEffect(() => {
    setSearchTerm(search);
  }, [search]);

  function handleSearch(key: string, value: string) {
    setSearch((prev) => {
      const d = Object.fromEntries(prev.entries());

      if (!value) {
        delete d[key];
        return d;
      }

      return { ...d, [key]: value };
    });
    targetRef?.current?.scrollIntoView?.({ behavior: 'smooth' });
  }

  const canReset = selectedCategory?.id || sortBy !== 'newest' || selectedZone?.id || search;

  return (
    <>
      <div className='relative h-[95dvh]'>
        <div className='absolute inset-0'>
          <div className='pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-black/90 to-black/80 mix-blend-multiply' />
          <ImageWithPlaceholder
            alt='explore hero'
            className='h-full w-full object-cover'
            src={section?.sectionBackground?.url}
          />
        </div>
        <div className='container-padding container h-full w-full'>
          <div className='container relative flex h-full w-full flex-col gap-4'>
            <div className='absolute inset-x-0 bottom-20 px-3 xl:px-0'>
              <div
                className='flex h-full w-full flex-col items-stretch justify-between gap-6 sm:flex-row sm:gap-10'
                style={{
                  alignItems: 'normal',
                }}
              >
                <div className='space-y-6 lg:space-y-10 rtl:space-y-4'>
                  {section?.title && <HeroTitle className='uppercase' title={section?.title} />}
                </div>

                <div className='flex max-w-4xl flex-1 flex-col justify-between gap-4 space-y-2 py-2 text-[#A1A1AA]'>
                  <div className='flex max-w-2xl items-end justify-end md:ml-auto'>
                    <Typography as='p' className='!text-body-S xl:!text-body-M 2xl:!text-body-L'>
                      {section?.subtitle && section?.subtitle}
                    </Typography>
                  </div>

                  <div className='flex flex-col gap-2 xl:flex-row'>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        push(`/${locale}/explore?q=${encodeURIComponent(searchTerm)}`);
                        handleSearch('q', searchTerm);
                      }}
                      className='relative w-full flex-1 py-0 xl:w-auto'
                    >
                      <Input
                        value={searchTerm}
                        type='text'
                        onChange={(v) => {
                          const text = sanitizeInput(v);
                          setSearchTerm(text);
                        }}
                        placeholder={t('global_search_placeholder')}
                        className={twMerge(
                          'h-9 w-full !text-ellipsis rounded-full border-white bg-white/20 px-4 py-0 text-base text-white placeholder:text-white/60 hover:bg-white/20 focus-visible:bg-white/20 ltr:!pr-10 rtl:!pl-10',
                          canReset ? '' : ''
                        )}
                      />
                      <div className='absolute top-1/2 mt-0.5 -translate-y-1/2 ltr:right-2 rtl:left-2'>
                        <Button type='submit' className='size-6 rounded-full p-0'>
                          <SearchIcon className='w-4' />
                        </Button>
                      </div>
                    </form>
                    <div className='-mx-5 xl:mx-0'>
                      <div
                        key={locale}
                        className='flex flex-row items-center gap-2 overflow-x-auto px-5 xl:overflow-x-clip xl:!px-0 ltr:pr-5 rtl:pl-5'
                      >
                        <FilterSelect
                          key={selectedZone?.slug ? `${selectedZone?.slug}-${locale}` : undefined}
                          options={zoneOptions || []}
                          value={selectedZone ? selectedZone?.slug : 'all'}
                          onChange={(val) => {
                            if (val.id !== 'all') {
                              handleSearch('zone', val.id);
                            } else {
                              handleSearch('zone', '');
                            }
                          }}
                        />

                        <FilterSelect
                          key={
                            selectedCategory?.slug
                              ? `${selectedCategory?.slug}-${locale}`
                              : undefined
                          }
                          options={categoryOptions || []}
                          value={selectedCategory ? selectedCategory?.slug : 'all'}
                          onChange={(val) => {
                            if (val.id !== 'all') {
                              handleSearch('category', val.id);
                            } else {
                              handleSearch('category', '');
                            }
                          }}
                        />

                        <FilterSelect
                          key={sortBy ? `${sortBy}-${locale}` : undefined}
                          options={sortOptions}
                          value={sortBy as string}
                          onChange={(val) => {
                            handleSearch('sort', val.id);
                          }}
                        />
                      </div>
                    </div>

                    {canReset && (
                      <Button
                        onClick={() => {
                          push(pathname);
                          targetRef?.current?.scrollIntoView?.({ behavior: 'smooth' });
                        }}
                        className='w-full shrink-0 rounded-full bg-white/20 px-6 py-1 hover:bg-white/10 xl:w-auto'
                      >
                        {t('reset')}
                        <span>
                          <svg
                            width='14'
                            height='14'
                            viewBox='0 0 11 11'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              d='M2.55078 0.795532C2.6461 0.395922 3.15579 0.274885 3.41992 0.589478L3.69629 0.919556C4.32737 0.650903 5.02083 0.499634 5.75 0.499634C8.64939 0.499634 10.9998 2.85028 11 5.74963C11 8.64913 8.6495 10.9996 5.75 10.9996C2.85051 10.9996 0.5 8.64913 0.5 5.74963C0.500028 5.25731 0.567593 4.78058 0.694336 4.32874L1.65723 4.59924C1.55325 4.97 1.50003 5.35578 1.5 5.74963C1.5 8.09684 3.40279 9.99963 5.75 9.99963C8.09721 9.99963 10 8.09684 10 5.74963C9.99983 3.40257 8.09711 1.49963 5.75 1.49963C5.27469 1.49963 4.81255 1.58005 4.37598 1.72913L4.64941 2.05432C4.91396 2.3696 4.70485 2.85232 4.29395 2.87561L2.69434 2.96545C2.36023 2.98439 2.10232 2.6767 2.17969 2.3512L2.55078 0.795532Z'
                              fill='#A1A1AA'
                            />
                          </svg>
                        </span>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExploreHeroSection;

type FilterSelectProps = {
  options: { id: string; value: string; text: string }[];
  value?: string;
  onChange: (value: SelectOption) => void;
};

function FilterSelect({ options, value, onChange }: FilterSelectProps) {
  return (
    <Select
      groupClassName='[&>div>button]:-top-1 ltr:[&>div>button]:right-2 shrink-0'
      className='text-body-XS md:text-body-S max-w-40 truncate rounded-full border border-white px-4 py-1 font-semibold text-white placeholder:text-white ltr:pr-[20%] rtl:pl-[20%]'
      options={options}
      autoSelect
      value={value}
      onChange={onChange}
    />
  );
}
